import type { PropsWithChildren } from 'react';
import React, { useEffect, useState } from 'react';
import { isDev } from '@zg-rentals/environment-utils';
import { useBrand, useLogger, useRentalManagerUser, useRouter, useStaticApp, useZgApi } from '../hooks';
import { AuthCheck } from '@zg-rentals/particles-js-auth';
import { RentalManagerUserInfoWebApiHandler_get } from '@zg-rentals/ts-rental-manager-api-client';
import { BlockedUserModal } from '@zg-rentals/blocked-user-modal';
import { getGlobalMonitor } from '@zg-rentals/monitor-browser';
import DevCookieControls from './DevCookieControls/DevCookieControls';
import type { RentalManagerAuthCheckUserInfoFeaturesWebApiDto } from '@zg-rentals/ts-rental-manager-api-client';
import { disableLogError } from '@zg-rentals/log-error';

const ERROR_TYPE_MFA_STEP_UP_ATTEMPT = 'MfaStepUpAttempt';

export type AuthCheckWrapperProps = PropsWithChildren<{
  variant?: string;
}>;

/**
 *
 * @deprecated use AuthRoute in place of Auth, do not use this component as a wrapper anymore
 */
export const AuthCheckWrapper = ({ children, variant }: AuthCheckWrapperProps) => {
  const [devModalOpen, setDevModalOpen] = React.useState(false);
  const isNonDevEnv = !isDev();
  const zgApi = useZgApi();
  const brand = useBrand();
  const { initialAuthComplete } = useStaticApp();
  const logger = useLogger();
  const router = useRouter();
  const { user, updateUser } = useRentalManagerUser();
  const {
    loggedIn,
    isTwoFactorAuthEnabled,
    isTwoFactorAuthenticated,
    hasStrongAuthentication,
    userToken,
    impersonatorUserToken,
    impersonatorEmail,
  } = user;
  const [isReadyToClientSideRedirect, setIsReadyToClientSideRedirect] = useState(false);

  useEffect(() => {
    RentalManagerUserInfoWebApiHandler_get({
      includeCustomers: true,
      // @ts-ignore
      includeSummary: true,
      includeCanClaimFreeListings: true,
    })
      .then((apiRes) => {
        apiRes.features = apiRes.features || ({} as RentalManagerAuthCheckUserInfoFeaturesWebApiDto);
        updateUser(apiRes);
        return setIsReadyToClientSideRedirect(true);
      })
      .catch(() => {
        logger.error('Failed to auth user on front end');
        return setIsReadyToClientSideRedirect(true);
      });
  }, []);

  const handleNotLoggedIn = React.useCallback(() => {
    if (isNonDevEnv) {
      return (redirectUrl: string) => {
        if (variant) {
          redirectUrl += `${redirectUrl.includes('?') ? '&' : '?'}variant=${variant}`;
        }
        if (user.isInitialMultiFactorAuthenticationRequired) {
          redirectUrl += `${redirectUrl.includes('?') ? '&' : '?'}mfaStepUp=true`;
          getGlobalMonitor()?.count(
            { name: 'mfa step-up attempt total' },
            { name: `mfa step-up attempt type ${ERROR_TYPE_MFA_STEP_UP_ATTEMPT}` },
          );
        }
        try {
          disableLogError();
          window.stop();
        } catch (exception) {
          document.execCommand('Stop');
        }
        window.location.href = redirectUrl;
      };
    }
    return () => {
      if (zgApi.includes('localhost')) {
        setDevModalOpen(true);
      } else if (zgApi.includes('comet1') || zgApi.includes('qa')) {
        // dev
        const devLoginUrl = `${zgApi}/rental-manager/featureFlags`;
        // @ts-ignore
        window.location = devLoginUrl;
      } else {
        // local dev pointed at a prod-like zgApi backend
        const protocol = window.location.protocol.replace(':', '');
        const port = window.location.port || '3000';
        const devLoginUrl = `${zgApi}user/acct/login/?url=/rental-manager-api/forward%3Fprotocol=${protocol}%26port=${port}%26path=${window.location.pathname}`;
        // @ts-ignore
        window.location = devLoginUrl;
      }
    };
  }, [zgApi, isNonDevEnv, user.isInitialMultiFactorAuthenticationRequired]);

  if (!initialAuthComplete) return null;

  return (
    <>
      <AuthCheck
        brand={brand}
        loggedIn={loggedIn}
        hasStrongAuthentication={hasStrongAuthentication}
        impersonatorEmail={impersonatorEmail}
        impersonatorUserToken={impersonatorUserToken}
        isTwoFactorAuthEnabled={isTwoFactorAuthEnabled}
        isTwoFactorAuthenticated={isTwoFactorAuthenticated}
        isReadyToClientSideRedirect={isReadyToClientSideRedirect}
        isInWebView={false}
        requireStrongAuthentication={false}
        routing={router}
        userToken={userToken}
        onNotLoggedIn={handleNotLoggedIn()}
        zillowCustomLoginPagePath="/rental-manager/login/"
      >
        {children}
      </AuthCheck>
      {!isNonDevEnv && <DevCookieControls devModalOpen={devModalOpen} setDevModalOpen={setDevModalOpen} />}
      {user?.restrictions?.isAllowed === false && user != null && <BlockedUserModal user={user} />}
    </>
  );
};
